import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Field.js';
import { Modal } from '@/components/ui/Modal.js';
import { useToast } from '@/components/ui/Toast.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { errorMessage } from '@/lib/api-error.js';
import { queryKeys } from '@/lib/query-keys.js';
import {
  certificatesApi,
  type CertificateFormValues,
  type CertificateRecord,
} from './certificates-api.js';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Tahrirlash uchun — bo'sh bo'lsa yangi sertifikat yaratiladi. */
  record?: CertificateRecord | null;
  onSaved?: (record: CertificateRecord) => void;
};

const EMPTY: CertificateFormValues = {
  number: '',
  fullName: '',
  photoMediaId: '',
  courseTitle: '',
  hours: 0,
  studyStart: '',
  studyEnd: '',
  issuedAt: '',
};

/**
 * Sertifikat formasi.
 *
 * Saqlashda PDF DARHOL shakllanadi (server tomonda), shuning uchun tugma
 * «Saqlash va shakllantirish» deb nomlangan — xodim ikkinchi qadamni
 * kutmasin.
 *
 * Amal qilish muddati formada YO'Q: u berilgan sanadan 3 yil qilib
 * hisoblanadi (shablonda ham «3 yil» deb bosilgan).
 */
export function CertificateFormModal({ open, onClose, record, onSaved }: Props) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [values, setValues] = useState<CertificateFormValues>(EMPTY);

  useEffect(() => {
    if (!open) return;
    setValues(record ? toFormValues(record) : EMPTY);
  }, [open, record]);

  const save = useMutation({
    mutationFn: (input: CertificateFormValues) =>
      record ? certificatesApi.update(record._id, input) : certificatesApi.create(input),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.certificates.all });
      toast.success(record ? 'Sertifikat yangilandi' : 'Sertifikat yaratildi va PDF shakllandi');
      onSaved?.(saved);
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  function set<K extends keyof CertificateFormValues>(
    key: K,
    value: CertificateFormValues[K],
  ): void {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  const canSubmit =
    values.number.trim() !== '' &&
    values.fullName.trim().length >= 3 &&
    values.photoMediaId !== '' &&
    values.courseTitle.trim().length >= 3 &&
    values.hours > 0 &&
    values.studyStart !== '' &&
    values.studyEnd !== '' &&
    values.issuedAt !== '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={record ? `Sertifikat ${record.number}` : 'Yangi sertifikat'}
      description={
        record
          ? 'O‘zgartirilgach PDF qaytadan shakllanadi va eski fayl o‘chadi.'
          : 'Saqlanganda PDF avtomatik shakllanadi va ichiga QR joylanadi.'
      }
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Bekor qilish
          </Button>
          <Button
            loading={save.isPending}
            disabled={!canSubmit}
            onClick={() => save.mutate(values)}
          >
            Saqlash va shakllantirish
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label="Seriya raqami"
          required
          hint="Qog‘ozdagi raqam, masalan 0000976"
          value={values.number}
          onChange={(event) => set('number', event.target.value)}
          placeholder="0000976"
        />

        <Input
          label="Berilgan sana"
          required
          hint="Amal qilish muddati: +3 yil"
          type="date"
          value={values.issuedAt}
          onChange={(event) => set('issuedAt', event.target.value)}
        />

        <div className="sm:col-span-2">
          <Input
            label="F.I.Sh."
            required
            hint="Qog‘ozdagi yozuv bilan AYNAN bir xil"
            value={values.fullName}
            onChange={(event) => set('fullName', event.target.value)}
            placeholder="Abdurahmonov Shohruh Akmalovich"
          />
        </div>

        <div className="sm:col-span-2">
          <Input
            label="Kurs nomi"
            required
            value={values.courseTitle}
            onChange={(event) => set('courseTitle', event.target.value)}
            placeholder="Fuqaro aviatsiyasi uchuvchisiz uchish apparatlari operatori"
          />
        </div>

        <Input
          label="O‘qish boshlandi"
          required
          type="date"
          value={values.studyStart}
          onChange={(event) => set('studyStart', event.target.value)}
        />

        <Input
          label="O‘qish tugadi"
          required
          type="date"
          value={values.studyEnd}
          onChange={(event) => set('studyEnd', event.target.value)}
        />

        <Input
          label="Soat hajmi"
          required
          type="number"
          min={1}
          value={values.hours || ''}
          onChange={(event) => set('hours', Number(event.target.value))}
          placeholder="144"
        />

        <div className="sm:col-span-2">
          {/*
            Surat MAJBURIY: shablonda ramka bor va bo'sh qolsa hujjat
            tugallanmagan ko'rinadi. Bundan tashqari saytdagi surat —
            soxtalikka qarshi asosiy dalil (mijoz talabi).
          */}
          <MediaPicker
            label="3×4 surat (majburiy)"
            hint="Ramkaga to‘liq sig‘adi — oldindan 3×4 qilib kesilgani ma’qul"
            value={values.photoMediaId ? { mediaId: values.photoMediaId, alt: null } : null}
            onChange={(value) => set('photoMediaId', value?.mediaId ?? '')}
          />
        </div>
      </div>
    </Modal>
  );
}

/** `2026-08-12T00:00:00.000Z` → `2026-08-12` (`<input type="date">` uchun). */
function toDateInput(value: string): string {
  return value.slice(0, 10);
}

function toFormValues(record: CertificateRecord): CertificateFormValues {
  return {
    number: record.number,
    fullName: record.fullName,
    photoMediaId: record.photoMediaId,
    courseTitle: record.courseTitle,
    hours: record.hours,
    studyStart: toDateInput(record.studyStart),
    studyEnd: toDateInput(record.studyEnd),
    issuedAt: toDateInput(record.issuedAt),
  };
}
